import React, {
	useState,
	FunctionComponent,
	useEffect,
	ReactNode,
	useRef,
} from "react";
import cx from "classnames";
import * as styles from "./ExpandCollapse.module.scss";
import { StaticImage } from "gatsby-plugin-image";
import classNames from "classnames";
import { Typography } from "@components";
import { CaretDown } from "@assets/icons";
import { Colors } from "@helpers/Colors";

type IconTypes = "caret" | "plus";

type ExpandCollapseProps = {
	className?: string;
	style?: React.CSSProperties;
	title: JSX.Element;
	caret: "LIGHT" | "DARK";
	expandDefault?: boolean;
	iconSize?: 24 | 14;
	iconPosition?: "left" | "right";
	children?: ReactNode;
	icon?: IconTypes;
	fullWidth?: boolean;
	expanded?: boolean;
	id?: number;
	onClick?: (id: number) => void;
	expandedIconColor?: Colors;
};

const ExpandCollapse: FunctionComponent<ExpandCollapseProps> = ({
	id,
	className,
	style = {},
	title,
	caret,
	children,
	expandDefault = false,
	iconSize = 14,
	iconPosition = "right",
	icon = "caret",
	fullWidth = false,
	expanded,
	onClick,
	expandedIconColor,
}) => {
	const expandedTitle = useRef<HTMLDivElement | null>(null);
	const [isExpanded, setIsExpanded] = useState(false);

	useEffect(() => {
		setIsExpanded(expanded || false);
		setTimeout(() => {
			if (expanded && expandedTitle.current) {
				expandedTitle.current.scrollIntoView();
			}
		}, 200);
	}, [expanded, title]);

	const getIcon = () => {
		const map: {
			[key in IconTypes]: {
				light: { collapsed: JSX.Element; expanded: JSX.Element };
				dark: { collapsed: JSX.Element; expanded: JSX.Element };
			};
		} = {
			caret: {
				light: {
					collapsed: (
						<StaticImage
							loading="eager"
							src="../../assets/icons/caret-down-light.svg"
							alt="down arrow"
							className={cx(styles.isExpandCollapseIcon, {
								[styles.isExpandedIcon]: isExpanded,
							})}
							style={{
								minWidth: iconSize,
								width: iconSize,
								height: iconSize,
							}}
							width={24}
							height={24}
						/>
					),
					expanded: (
						<StaticImage
							loading="eager"
							src="../../assets/icons/caret-down-light.svg"
							alt="down arrow"
							className={cx(styles.isExpandCollapseIcon, {
								[styles.isExpandedIcon]: isExpanded,
							})}
							style={{
								minWidth: iconSize,
								width: iconSize,
								height: iconSize,
							}}
							width={24}
							height={24}
						/>
					),
				},
				dark: {
					collapsed: <CaretDown />,
					expanded: (
						<CaretDown
							style={{
								transform: "rotate(180deg)",
								transition: "transform 0.2s ease",
								color: expandedIconColor
									? `var(--${expandedIconColor})`
									: "",
							}}
						/>
					),
				},
			},
			plus: {
				light: {
					collapsed: (
						<div className={styles.collapsesibleIcon}>
							<Typography color="white" variant="HEADER2">
								+
							</Typography>
						</div>
					),
					expanded: (
						<div className={styles.collapsesibleIcon}>
							<Typography color="white" variant="HEADER2">
								-
							</Typography>
						</div>
					),
				},
				dark: {
					collapsed: (
						<StaticImage
							loading="eager"
							src="./plus.svg"
							alt="down arrow"
							className={cx(
								styles.isExpandCollapseIcon,
								styles.plusIcon,
								{ [styles.isExpandedIcon]: isExpanded }
							)}
							style={{
								minWidth: iconSize,
								width: iconSize,
								height: iconSize,
							}}
							width={24}
							height={24}
						/>
					),
					expanded: (
						<StaticImage
							loading="eager"
							src="./plus.svg"
							alt="down arrow"
							className={cx(
								styles.isExpandCollapseIcon,
								styles.plusIcon,
								{ [styles.isExpandedIcon]: isExpanded }
							)}
							style={{
								minWidth: iconSize,
								width: iconSize,
								height: iconSize,
							}}
							width={24}
							height={24}
						/>
					),
				},
			},
		};
		return map[icon][caret === "LIGHT" ? "light" : "dark"][
			isExpanded ? "expanded" : "collapsed"
		];
	};

	useEffect(() => {
		if (expandDefault) setIsExpanded(true);
	}, [expandDefault]);

	return (
		<div
			className={className}
			style={style}
			onClick={() => onClick?.(id || -1)}
		>
			<div
				ref={expandedTitle}
				className={classNames(styles.expandTitle, `title-${id}}`, {
					[styles.expandTitleRight]: iconPosition === "right",
					[styles.expandTitleFullWidth]: fullWidth,
				})}
				onClick={() => setIsExpanded(!isExpanded)}
			>
				{title}
				<div>{getIcon()}</div>
			</div>
			<div
				className={cx(styles.isExpandCollapse, {
					[styles.isExpanded]: isExpanded,
				})}
			>
				{children}
			</div>
		</div>
	);
};

export default ExpandCollapse;
